// Dashboard Module
import { api } from '../core/api-client.js';
import { showNotification } from '../utils/notifications.js';

let statsInterval = null;
let timerInterval = null;
let fleetHealthChart = null;

export async function initDashboard() {
    try {
        console.log('[Dashboard] Initializing...');

        // Start epoch timer
        startEpochTimer();

        // Load stats immediately
        await loadStats();

        // Refresh stats every 30 seconds
        statsInterval = setInterval(loadStats, 30000);

        console.log('[Dashboard] Initialization complete');
    } catch (error) {
        console.error('[Dashboard] Initialization error:', error);
    }
}

// Epoch timer - updates the clock in header
function startEpochTimer() {
    updateEpochTimer(); // Update immediately

    // Update every second
    timerInterval = setInterval(updateEpochTimer, 1000);
    console.log('[Dashboard] Epoch timer started');
}

function updateEpochTimer() {
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', { hour12: false });
    const timerElement = document.getElementById('epoch-timer');
    if (timerElement) {
        timerElement.textContent = timeString;
    }
}

// Fleet Health doughnut (Chart.js v3+). Destroy/recreate so SPA navigation stays in sync with /api/dashboard/stats.
export function renderFleetHealthChart(online, offline) {
    const canvas = document.getElementById('healthChart');
    if (!canvas || typeof Chart === 'undefined') {
        if (!canvas) return;
        console.warn('[Dashboard] Chart.js not loaded — Fleet Health chart skipped');
        return;
    }

    const on = Math.max(0, Number(online) || 0);
    const off = Math.max(0, Number(offline) || 0);

    if (fleetHealthChart) {
        fleetHealthChart.destroy();
        fleetHealthChart = null;
    }

    // [0,0] renders nothing useful; show a neutral ring
    const d0 = on === 0 && off === 0 ? [1, 0] : [on, off];
    const colors = on === 0 && off === 0 ? ['rgba(148,163,184,0.35)', 'rgba(148,163,184,0.08)'] : ['#00FF88', '#EF4444'];

    fleetHealthChart = new Chart(canvas, {
        type: 'doughnut',
        data: {
            labels: ['Online', 'Offline'],
            datasets: [
                {
                    data: d0,
                    backgroundColor: colors,
                    borderWidth: 0
                }
            ]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            cutout: '70%',
            plugins: {
                legend: { display: false }
            }
        }
    });
    window.__fleetHealthChart = fleetHealthChart;
}

async function loadStats() {
    try {
        const stats = await api.getStats();
        console.log('[Dashboard] Stats loaded:', stats);
        updateStatsUI(stats);
        updateHeaderStats(stats);

        const online = stats.fleet_online != null ? stats.fleet_online : stats.online;
        let offline = stats.fleet_offline;
        if (offline == null && stats.active_fleet != null && online != null) {
            offline = Math.max(0, Number(stats.active_fleet) - Number(online));
        }
        if (offline == null) offline = 0;
        renderFleetHealthChart(online, offline);
    } catch (error) {
        console.error('[Dashboard] Failed to load stats:', error);
        // Don't show notification for stats - it's not critical
    }
}

function updateStatsUI(stats) {
    try {
        // Update stat cards on dashboard
        const statElements = {
            'total-agents': stats.total_agents || stats.active_fleet || 0,
            'active-threats': stats.active_threats || stats.threats_neutralized || 0,
            'total-clients': stats.total_clients || 0,
            'active-licenses': stats.active_licenses || 0,
            // KPI cards on dashboard
            'stat-active-fleet': stats.active_fleet || stats.total_agents || 0,
            'stat-online': stats.online || 0,
            'stat-warning': stats.warning || 0,
            'stat-critical': stats.critical || 0,
            'stat-threats-total': stats.threats_neutralized || stats.active_threats || 0,
            'stat-threats-today': stats.threats_today || 0
        };

        Object.entries(statElements).forEach(([id, value]) => {
            const element = document.getElementById(id);
            if (element) {
                element.textContent = value;
            }
        });

        const revenueEl = document.getElementById('stat-revenue');
        if (revenueEl) {
            const mrr = Number(stats.revenue_mrr) || 0;
            revenueEl.textContent = '$' + mrr.toLocaleString(undefined, { maximumFractionDigits: 0 });
        }
        const renewalEl = document.getElementById('stat-renewal');
        if (renewalEl) {
            const rate = Number(stats.renewal_rate) || 0;
            renewalEl.textContent = Math.round(rate) + '%';
        }
    } catch (error) {
        console.error('[Dashboard] Error updating stats UI:', error);
    }
}

// Update header statistics (Agents count, Threats count)
function updateHeaderStats(stats) {
    try {
        const headerAgentCount = document.getElementById('header-agent-count');
        const headerThreatCount = document.getElementById('header-threat-count');

        if (headerAgentCount) {
            headerAgentCount.textContent = stats.active_fleet || stats.total_agents || 0;
        }
        if (headerThreatCount) {
            headerThreatCount.textContent = stats.threats_neutralized || stats.active_threats || 0;
        }

        console.log('[Dashboard] Header stats updated:', {
            agents: stats.active_fleet || stats.total_agents || 0,
            threats: stats.threats_neutralized || stats.active_threats || 0
        });
    } catch (error) {
        console.error('[Dashboard] Error updating header stats:', error);
    }
}

// Cleanup function
export function destroyDashboard() {
    if (statsInterval) {
        clearInterval(statsInterval);
        statsInterval = null;
    }
    if (timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
    }
    if (fleetHealthChart) {
        fleetHealthChart.destroy();
        fleetHealthChart = null;
    }
    console.log('[Dashboard] Cleanup complete');
}

export { loadStats };

